import { useEffect, useState } from "react";
import { API, api } from "@/App";
import { BookOpen, Check, CheckCircle2, Copy, Download, Loader2, XCircle } from "lucide-react";
import { CheckoutHeader } from "@/components/CheckoutPage";

export const CopyKey = ({ value, testid }) => {
  const [ok, setOk] = useState(false);
  return (
    <div className="flex items-center gap-2 bg-black border border-[#2a2f3d] px-3 py-2">
      <code className="font-mono text-sm md:text-base break-all text-[#e69d27] flex-1" data-testid={testid}>{value}</code>
      <button type="button" onClick={() => { navigator.clipboard?.writeText(value); setOk(true); }} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1" data-testid={`${testid}-copy`}>
        {ok ? <Check size={12} /> : <Copy size={12} />}{ok ? "copied" : "copy"}
      </button>
    </div>
  );
};

const P = {
  denoise: { name: "Denoise", color: "#00e5ff", bin: "denoise-", manual: "Denoise" },
  skin: { name: "Skin", color: "#ff7a9c", bin: "skin-", manual: "Skin" },
  texture: { name: "Texture", color: "#ff6a3d", bin: "texture-", manual: "Texture" },
  reactor: { name: "Reactor", color: "#c48bff", bin: "reactor-", manual: "Reactor" },
  deflicker: { name: "Deflicker", color: "#a3ff4f", bin: "deflicker-", manual: "Deflicker" },
  genalpha: { name: "Gen Alpha", color: "#e69d27", bin: "", manual: "GenAlpha" },
  recast: { name: "Recast", color: "#4ff2c2", bin: "recast-", manual: "Recast" },
};
const OS = [["mac-universal", "macOS"], ["windows-x64", "Windows"], ["linux-x86_64", "Linux"]];
const OFFLINE = ["denoise", "skin", "texture", "reactor", "deflicker"];
const FAL = <a className="text-[#00e5ff] underline" href="https://fal.ai/dashboard/keys" target="_blank" rel="noreferrer">fal.ai/dashboard/keys</a>;

const NextStep = ({ product }) => (
  <p className="text-sm text-[#9ba3b4]">
    {OFFLINE.includes(product)
      ? <>Next: install, copy the Machine ID from the {P[product].name} panel in Resolve, then <a className="text-[#00e5ff] underline" href="/activate">get your activation code</a>.</>
      : <>Next: install, paste the key into the Licence key field of the {P[product].name} panel in Resolve, then your own fal.ai API key (from {FAL}). No activation code needed.</>}
  </p>
);

const Downloads = ({ product }) => (
  <div className="flex flex-wrap gap-2" data-testid={`confirm-downloads-${product}`}>
    {OS.map(([t, label]) => (
      <a key={t} href={`${API}/binary/${P[product].bin}${t}`} className="px-3 py-1.5 text-xs border border-[#2a2f3d] hover:border-white flex items-center gap-1.5 transition-colors" data-testid={`confirm-dl-${product}-${t}`}><Download size={12} /> {label}</a>
    ))}
    {P[product].manual && <a href={`/downloads/KolorKlinic-${P[product].manual}-Manual.pdf`} download className="px-3 py-1.5 text-xs border flex items-center gap-1.5 transition-colors hover:bg-white/10" style={{ borderColor: P[product].color, color: P[product].color }} data-testid={`confirm-manual-${product}`}><BookOpen size={12} /> Manual · PDF</a>}
  </div>
);

const ProductCard = ({ product, keys }) => (
  <section className="border border-[#2a2f3d] bg-[#0e1013] p-6 space-y-4" data-testid={`confirm-card-${product}`}>
    <div className="h-1 w-12" style={{ background: P[product].color }} />
    <h2 className="font-display uppercase text-2xl leading-none">{P[product].name}</h2>
    {keys.map((k) => (
      <div key={k.key} className="space-y-1">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#9ba3b4]">{k.seat ? `Seat ${k.seat} · ` : ""}Licence key</div>
        <CopyKey value={k.key} testid={k.seat ? `payment-result-key-${k.seat}-${product}` : keys.length > 1 || k.multi ? `payment-result-key-${product}` : "payment-result-key"} />
      </div>
    ))}
    <NextStep product={product} />
    <Downloads product={product} />
  </section>
);

function groups(r) {
  if (r.kind === "bundle" || r.kind === "aipack") {
    const g = {};
    r.keys.forEach((k) => { (g[k.product] = g[k.product] || []).push({ key: k.key, seat: r.seats > 1 ? k.seat : null, multi: true }); });
    return Object.entries(g);
  }
  return [[r.kind === "plan" ? "genalpha" : r.kind, [{ key: r.key }]]];
}

function Result({ r, email }) {
  if (r.kind === "topup") return <p className="text-lg" data-testid="payment-result-topup">{r.minutes} extra minutes were added to your Gen Alpha key for this month.</p>;
  const g = groups(r);
  return (
    <div className="space-y-6" data-testid={r.kind === "bundle" || r.kind === "aipack" ? "payment-result-bundle" : "payment-result-single"}>
      <p className="text-[#d4d8e0]">{g.length > 1 || r.seats > 1 ? "Your licence keys are below" : "Your licence key is below"}{email ? <>, and a copy was emailed to <b className="text-white">{email}</b></> : null}.</p>
      <div className={`grid gap-6 ${g.length > 1 ? "md:grid-cols-2" : ""}`}>
        {g.map(([product, keys]) => <ProductCard key={product} product={product} keys={keys} />)}
      </div>
    </div>
  );
}

const fmt = (c, cur) => new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "eur").toUpperCase() }).format(c / 100);

const Paid = ({ p }) => (
  <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm text-[#9ba3b4] border-y border-[#2a2f3d] py-3" data-testid="payment-paid-summary">
    <span>Paid <b className="text-white" data-testid="payment-paid-total">{fmt(p.total, p.currency)}</b></span>
    {p.discount > 0 && <span data-testid="payment-paid-discount">Promo −{fmt(p.discount, p.currency)}</span>}
    {p.tax > 0 && <span data-testid="payment-paid-tax">incl. tax {fmt(p.tax, p.currency)}</span>}
    <span>Receipt sent by Stripe to your email</span>
  </div>
);

function usePayment(sid, cancelled) {
  const [st, setSt] = useState(null);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    if (cancelled || !sid || st?.result || tries > 30) return undefined;
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/store/status/${sid}`); setSt(data); } catch { setSt({ payment_status: "error" }); }
      setTries((n) => n + 1);
    }, tries ? 2000 : 0);
    return () => clearTimeout(t);
  }, [sid, tries, st, cancelled]);
  return { st, failed: tries > 30 || st?.payment_status === "error" };
}

export default function PaymentPage() {
  const cancelled = window.location.pathname.startsWith("/payment/cancel");
  const sid = new URLSearchParams(window.location.search).get("session_id");
  const { st, failed } = usePayment(sid, cancelled);
  if (st?.result) sessionStorage.removeItem("kk_checkout");
  return (
    <div className="min-h-screen bg-[#0b0c0e] text-white" data-testid={cancelled ? "payment-cancel" : "payment-success"}>
      <CheckoutHeader back="/" badge={cancelled ? "checkout cancelled" : st?.result ? "order confirmed" : "confirming"} Icon={cancelled ? XCircle : CheckCircle2} />
      <main className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        {cancelled ? (
          <>
            <h1 className="font-display uppercase text-4xl flex gap-3 items-center"><XCircle className="text-[#ff453a]" /> Payment cancelled</h1>
            <p className="text-[#9ba3b4]">No charge was made. <a href="/#pricing" className="underline text-white" data-testid="payment-cancel-pricing-link">Back to pricing</a>.</p>
          </>
        ) : (
          <>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#9ba3b4] mb-2">{st?.result ? "Your order" : "Almost there"}</div>
              <h1 className="font-display uppercase text-4xl sm:text-5xl leading-none">{st?.result ? "Thank you!" : "Confirming payment"}</h1>
            </div>
            {st?.paid?.total != null && <Paid p={st.paid} />}
            {st?.result ? <Result r={st.result} email={st.email} /> : (
              <p className="flex items-center gap-2 text-[#9ba3b4]" data-testid="payment-pending">
                {failed ? "We couldn't confirm the payment yet. Your key will arrive by email once it clears." : <><Loader2 className="spin" size={16} /> Waiting for Stripe…</>}
              </p>
            )}
            <a href="/" className="btn-ghost inline-block px-4 py-2 text-xs" data-testid="back-home-link">Back to KolorKlinic</a>
          </>
        )}
      </main>
    </div>
  );
}
